/**
 * Wan 2.1 Video Generation Constants
 *
 * Wan 2.1 - FREE local video generation with excellent quality
 */

export const WAN21_MODELS = {
  T2V_14B: 'Wan-AI/Wan2.1-T2V-14B',
  T2V_1_3B: 'Wan-AI/Wan2.1-T2V-1.3B',
  I2V_14B: 'Wan-AI/Wan2.1-I2V-14B-720P',
  I2V_480P: 'Wan-AI/Wan2.1-I2V-14B-480P',
} as const;

export type Wan21Model = (typeof WAN21_MODELS)[keyof typeof WAN21_MODELS];

export const WAN21_RESOLUTIONS = {
  '720p': { width: 1280, height: 720, label: '720p HD' },
  '480p': { width: 854, height: 480, label: '480p SD' },
  '360p': { width: 640, height: 360, label: '360p (Low VRAM)' },
} as const;

export type Wan21Resolution = keyof typeof WAN21_RESOLUTIONS;

export interface Wan21Config {
  model: Wan21Model;
  resolution: Wan21Resolution;
  num_frames: number;
  fps: number;
  guidance_scale: number;
  negative_prompt?: string;
}

export const DEFAULT_WAN21_CONFIG: Wan21Config = {
  model: WAN21_MODELS.T2V_14B,
  resolution: '720p',
  num_frames: 81,
  fps: 24,
  guidance_scale: 6.0,
};

// VRAM requirements in GB
export const WAN21_VRAM_REQUIREMENTS = {
  [WAN21_MODELS.T2V_14B]: 12,
  [WAN21_MODELS.T2V_1_3B]: 8,
  [WAN21_MODELS.I2V_14B]: 12,
  [WAN21_MODELS.I2V_480P]: 10,
} as const;

// Frame options for duration selection
export const WAN21_FRAME_OPTIONS = [
  { frames: 25, label: '~1.0s', vramRequired: 8 },
  { frames: 49, label: '~2.0s', vramRequired: 10 },
  { frames: 81, label: '~3.4s', vramRequired: 12 },
  { frames: 121, label: '~5.0s', vramRequired: 16 },
] as const;

/**
 * Wan 2.1 is always FREE (local execution)
 */
export function calculateWan21Cost(): number {
  return 0;
}

/**
 * Get recommended model based on VRAM
 */
export function getRecommendedModel(vramGB: number): Wan21Model {
  if (vramGB >= 12) {
    return WAN21_MODELS.T2V_14B;
  } else if (vramGB >= 8) {
    return WAN21_MODELS.T2V_1_3B;
  }
  return WAN21_MODELS.T2V_1_3B;
}

/**
 * Get recommended resolution based on VRAM
 */
export function getRecommendedResolution(vramGB: number): Wan21Resolution {
  if (vramGB >= 16) return '720p';
  if (vramGB >= 12) return '480p';
  return '360p';
}

/**
 * Check if GPU has enough VRAM for Wan 2.1
 */
export function hasEnoughVRAM(vramGB: number): boolean {
  return vramGB >= 8.0;
}

/**
 * Get duration string from frame count
 */
export function getDurationFromFrames(frames: number, fps: number = 24): string {
  return `~${(frames / fps).toFixed(1)}s`;
}
